import axios from 'axios';
import { mockWatchfaces, mockFAQs } from '../utils/mockData';
import { getPaymentMode } from '../utils/productType';
const API_BASE_URL = 'https://gapps.orcatec.net/api';
/** compatibleDeviceIds kommt als Array oder als kommagetrennter String aus der API */
function parseDeviceIds(value) {
    if (Array.isArray(value))
        return value.map(String).filter(Boolean);
    if (typeof value === 'string' && value.trim())
        return value.split(',').map((v) => v.trim()).filter(Boolean);
    return undefined;
}
class ApiService {
    constructor() {
        this.productsCache = null;
        this.productsCacheMaxAge = 5 * 60 * 1000; // 5 minutes
        this.api = axios.create({
            baseURL: API_BASE_URL,
            timeout: 15000,
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        });
    }
    async getWatchfaces() {
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            return { success: true, data: mockWatchfaces };
        }
        catch {
            return { success: false, data: [], error: 'Failed to fetch watchfaces' };
        }
    }
    async getWatchface(id) {
        try {
            await new Promise(resolve => setTimeout(resolve, 800));
            const watchface = mockWatchfaces.find(w => w.id === id);
            if (watchface) {
                return { success: true, data: watchface };
            }
            return { success: false, data: {}, error: 'Watchface not found' };
        }
        catch {
            return { success: false, data: {}, error: 'Failed to fetch watchface' };
        }
    }
    async getFAQs() {
        try {
            await new Promise(resolve => setTimeout(resolve, 600));
            return { success: true, data: mockFAQs };
        }
        catch {
            return { success: false, data: [], error: 'Failed to fetch FAQs' };
        }
    }
    async subscribeNewsletter(email) {
        try {
            const response = await axios.get('https://api.orcatec.net/newsletter/setNewsletter.php', {
                params: { email },
            });
            return {
                success: true,
                data: true,
                message: response.data || 'Successfully subscribed to newsletter',
            };
        }
        catch {
            return { success: false, data: false, error: 'Failed to subscribe to newsletter' };
        }
    }
    async processPurchase(watchfaceId, paymentData) {
        try {
            const response = await this.api.post('/purchase', { watchfaceId, paymentData });
            return { success: true, data: response.data, message: 'Purchase processed successfully' };
        }
        catch {
            return { success: false, data: null, error: 'Purchase failed' };
        }
    }
    getCoverImagePath(weblink, imageFileName) {
        const weblinkClean = weblink.replace('/', '');
        if (imageFileName) {
            return `/images/${weblinkClean}/${imageFileName}`;
        }
        return `/images/${weblinkClean}/cover.png`;
    }
    isCacheValid() {
        if (!this.productsCache)
            return false;
        return Date.now() - this.productsCache.timestamp < this.productsCacheMaxAge;
    }
    async getProducts() {
        if (this.isCacheValid()) {
            return { success: true, data: this.productsCache.data };
        }
        try {
            const response = await axios.get(`${API_BASE_URL}/products.php`, {
                timeout: 15000,
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });
            if (response.data && response.data.success) {
                const products = response.data.data.map((product) => {
                    return {
                        ...product,
                        id: product.id,
                        name: product.name,
                        weblink: product.weblink,
                        coverImage: this.getCoverImagePath(product.weblink, product.coverImage || product.image),
                        price: product.price,
                        avgRating: product.avgRating,
                        latestExternalVersion: product.latestExternalVersion,
                        supportedDevicesCount: product.supportedDevicesCount,
                        garminpay: product.garminpay,
                        is_vx: product.is_vx,
                        promoted: product.promoted ?? 0,
                        storelink: product.storelink,
                        triallink: product.triallink,
                        active: product.active || 1,
                        image: product.coverImage || product.image,
                        releasedate: product.releasedate || null,
                        lastupdate: product.lastupdate || null,
                        description: product.name,
                        currency: '€',
                        showThemeInfo: true,
                        vxLink: getPaymentMode(product.is_vx) === 1 ? product.storelink : undefined,
                        apiName: product.apiName || '',
                        code: product.code || '',
                        GID: product.GID || '',
                        paddleproductid: product.paddleproductid || null,
                        paddlepriceid: product.paddlepriceid || null,
                        themes: product.themes || [],
                        compatibleDeviceIds: parseDeviceIds(product.compatibleDeviceIds),
                        eliteStorelink: product.elite_storelink || undefined,
                        vxVersion: product.vx_version || undefined,
                        eliteVersion: product.elite_version || undefined,
                        eliteId: product.elite_id || undefined
                    };
                });
                this.productsCache = { data: products, timestamp: Date.now() };
                return { success: true, data: products };
            }
            return { success: false, data: [], error: 'API returned unsuccessful response' };
        }
        catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('API request failed:', error);
            }
            return {
                success: false,
                data: [],
                error: error.response?.data?.error || error.message || 'Failed to fetch products from API'
            };
        }
    }
    async getProduct(id) {
        try {
            const productsResponse = await this.getProducts();
            if (productsResponse.success) {
                const product = productsResponse.data.find(p => p.id === id);
                if (product) {
                    return { success: true, data: product };
                }
                return { success: false, data: {}, error: 'Product not found' };
            }
            return { success: false, data: {}, error: productsResponse.error || 'Failed to fetch products' };
        }
        catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('Failed to fetch product:', error);
            }
            return { success: false, data: {}, error: error.message || 'Failed to fetch product from API' };
        }
    }
    normalizeWeblink(weblink) {
        return (weblink || '').replace(/\/$/, '').toLowerCase();
    }
    async getProductByIdOrWeblink(idOrWeblink) {
        if (!idOrWeblink || !idOrWeblink.trim()) {
            return { success: false, data: {}, error: 'Product not found' };
        }
        const input = idOrWeblink.trim();
        if (/^\d+$/.test(input)) {
            return this.getProduct(parseInt(input, 10));
        }
        try {
            const productsResponse = await this.getProducts();
            if (!productsResponse.success) {
                return { success: false, data: {}, error: productsResponse.error || 'Failed to fetch products' };
            }
            const slug = this.normalizeWeblink(input);
            const product = productsResponse.data.find(p => this.normalizeWeblink(p.weblink) === slug);
            if (product) {
                return { success: true, data: product };
            }
            return { success: false, data: {}, error: 'Product not found' };
        }
        catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('Failed to fetch product by weblink:', error);
            }
            return { success: false, data: {}, error: error.message || 'Failed to fetch product from API' };
        }
    }
    async getColorThemes(productId) {
        try {
            const productResponse = await this.getProduct(productId);
            if (productResponse.success && productResponse.data.themes) {
                const themes = (productResponse.data.themes || []).map((theme) => ({
                    id: theme.id,
                    productId: productId,
                    name: theme.name,
                    themeString: theme.themestring || theme.themeString,
                    image: theme.image,
                    sortOrder: theme.id
                }));
                return { success: true, data: themes };
            }
            return { success: false, data: [], error: productResponse.error || 'No themes found for this product' };
        }
        catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('Failed to fetch color themes:', error);
            }
            return { success: false, data: [], error: error.message || 'Failed to fetch color themes from API' };
        }
    }
}
export const apiService = new ApiService();
export default ApiService;
